import React from "react";
import { FiUsers, FiAward, FiHeart, FiBookOpen, FiCheck } from "react-icons/fi";
import adminImg from "../../assets/Admin.png";
import teachersImg from "../../assets/Teachers.png";
import parentsImg from "../../assets/Parents.png";

import ReportSheetCard from "./ReportSheetCard";

export const LandingWhoCan: React.FC = () => {
  const personas = [
    {
      title: "School Administrators",
      subtitle: "Full Campus Governance",
      icon: <FiAward size={28} className="text-blue-600" />,
      image: adminImg,
      bgColor: "bg-blue-50/70",
      accentColor: "border-blue-200",
      benefits: [
        "Automated fee invoicing & payment tracking",
        "Streamlined staff, subject & classroom allocation",
        "Comprehensive result sheet generation & analytics",
        "Authorized student pickup governance",
      ],
    },
    {
      title: "Teachers & Educators",
      subtitle: "Smart Classroom Operations",
      icon: <FiBookOpen size={28} className="text-indigo-600" />,
      image: teachersImg,
      bgColor: "bg-indigo-50/70",
      accentColor: "border-indigo-200",
      benefits: [
        "Alleviate teacher burnout with 1-click grade entries",
        "Instant digital attendance recording",
        "Simplified curriculum & activity logging",
        "Direct parent updates & progress notifications",
      ],
    },
    {
      title: "Parents & Guardians",
      subtitle: "Real-Time Peace of Mind",
      icon: <FiHeart size={28} className="text-rose-600" />,
      image: parentsImg,
      bgColor: "bg-rose-50/70",
      accentColor: "border-rose-200",
      benefits: [
        "Real-time tracking of daily school & classroom activities",
        "Instant payment proof uploads & digital fee receipts",
        "Secure student pickup passcodes & authorization",
        "Direct communication with school management",
      ],
    },
  ];

  return (
    <section className="py-24 bg-white relative overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-16">
        
        {/* Section Header */}
        <div className="text-center space-y-4 max-w-3xl mx-auto" data-aos="fade-up">
          <span className="text-xs font-black uppercase tracking-widest text-indigo-600 bg-indigo-50 px-4 py-1.5 rounded-full border border-indigo-100">
            Tailored Solutions
          </span>
          <h2 className="text-3xl sm:text-5xl font-black text-slate-900 tracking-tight">
            Who Can Use Pendulum Digital?
          </h2>
          <p className="text-sm sm:text-base text-slate-600 font-medium">
            Our platform facilitates efficient management across all stakeholders with ease and simplicity.
          </p>
        </div>

        {/* Persona Cards Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {personas.map((persona, idx) => (
            <div
              key={idx}
              data-aos="fade-up"
              data-aos-delay={idx * 150}
              className={`rounded-[2.5rem] p-8 bg-slate-50/80 border ${persona.accentColor} shadow-sm hover:shadow-xl transition-all duration-300 flex flex-col justify-between group relative overflow-hidden`}
            >
              <div className="space-y-6">
                
                {/* Header */}
                <div className="flex items-center gap-4">
                  <div className={`w-14 h-14 rounded-2xl ${persona.bgColor} flex items-center justify-center border ${persona.accentColor}`}>
                    {persona.icon}
                  </div>
                  <div>
                    <h3 className="text-xl font-black text-slate-900 group-hover:text-blue-600 transition-colors">
                      {persona.title}
                    </h3>
                    <span className="text-xs font-bold text-slate-400">
                      {persona.subtitle}
                    </span>
                  </div>
                </div>

                {/* Persona Illustration Image */}
                <div className="h-48 sm:h-52 rounded-2xl bg-white border border-slate-200/80 overflow-hidden shadow-sm">
                  <img
                    src={persona.image}
                    alt={persona.title}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                    onError={(e) => {
                      (e.target as HTMLElement).style.display = "none";
                    }}
                  />
                </div>

                {/* Benefits List */}
                <div className="space-y-2.5 pt-2">
                  {persona.benefits.map((b, bIdx) => (
                    <div key={bIdx} className="flex items-start gap-2.5 text-xs text-slate-700 font-medium">
                      <div className="w-4 h-4 rounded-full bg-emerald-100 text-emerald-600 flex items-center justify-center shrink-0 mt-0.5 font-bold">
                        <FiCheck size={11} />
                      </div>
                      <span>{b}</span>
                    </div>
                  ))}
                </div>

              </div>

              {/* Card Footer Quote */}
              <div className="pt-6 mt-6 border-t border-slate-200/60 text-[11px] font-bold text-slate-400 italic">
                Designed for seamless collaboration and modern digital learning.
              </div>

            </div>
          ))}
        </div>

        <ReportSheetCard />

      </div>
    </section>
  );
};

export default LandingWhoCan;
